/**
 * AddressAutocompleteInput
 * Native Google Places Autocomplete input for street addresses.
 * Emits { street, city, state, zip, country, lat, lng } when a place is selected.
 */
import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  getAddressAutocompleteCountryCode,
  getDefaultAddressCountryCode,
} from "../utils/addressAutocompleteSettings";

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let mapsLoadPromise = null;

function loadGoogleMapsScript() {
  if (window.google?.maps?.places) return Promise.resolve();
  if (mapsLoadPromise) return mapsLoadPromise;

  mapsLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-gmaps-autocomplete='1']");
    if (existing) {
      const poll = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(poll);
          resolve();
        }
      }, 100);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-gmaps-autocomplete", "1");
    script.onload = resolve;
    script.onerror = () => {
      mapsLoadPromise = null;
      reject(new Error("Google Maps failed to load"));
    };
    document.head.appendChild(script);
  });

  return mapsLoadPromise;
}

function parseAddressComponents(components = []) {
  const get = (type, nameType = "long_name") => {
    const hit = components.find((component) =>
      (component.types || []).includes(type),
    );
    return hit ? hit[nameType] || "" : "";
  };

  const streetNum = get("street_number");
  const route = get("route");
  const street = [streetNum, route].filter(Boolean).join(" ").trim();

  return {
    street,
    city:
      get("locality") ||
      get("sublocality_level_1") ||
      get("postal_town") ||
      get("administrative_area_level_2"),
    state: get("administrative_area_level_1", "short_name"),
    zip: get("postal_code"),
    country: get("country", "short_name"),
  };
}

const AddressAutocompleteInput = forwardRef(function AddressAutocompleteInput(
  {
    value,
    onChange,
    onAddressSelect,
    placeholder = "Street Address",
    required,
    style,
    inputStyle,
    className,
    inputClassName,
  },
  ref,
) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onAddressSelectRef = useRef(onAddressSelect);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);

  useEffect(() => {
    let mounted = true;

    if (!MAPS_API_KEY) {
      setStatusMessage("Google Maps API key is not configured");
      return undefined;
    }

    loadGoogleMapsScript()
      .then(async () => {
        if (!mounted || !inputRef.current) return;

        const countryCode =
          (await getAddressAutocompleteCountryCode().catch(() => null)) ||
          getDefaultAddressCountryCode();

        const options = {
          fields: ["address_components", "geometry", "formatted_address", "name"],
          types: ["address"],
        };

        if (countryCode) {
          options.componentRestrictions = { country: countryCode };
        }

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          options,
        );

        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current?.getPlace();
          if (!place) return;

          const parsed = parseAddressComponents(place.address_components || []);
          const streetValue =
            parsed.street ||
            place.name ||
            place.formatted_address ||
            inputRef.current?.value ||
            "";
          const lat = place.geometry?.location?.lat?.();
          const lng = place.geometry?.location?.lng?.();

          onChangeRef.current(streetValue);
          if (onAddressSelectRef.current) {
            onAddressSelectRef.current({
              ...parsed,
              street: streetValue,
              lat,
              lng,
            });
          }
          setStatusMessage("");
        });
      })
      .catch(() => {
        if (mounted) {
          setStatusMessage("Google Places could not be loaded");
        }
      });

    return () => {
      mounted = false;
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(
          autocompleteRef.current,
        );
      }
    };
  }, []);

  return (
    <div
      style={{ position: "relative", width: "100%", ...style }}
      className={className}
    >
      <input
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={{ width: "100%", boxSizing: "border-box", ...inputStyle }}
        className={inputClassName}
        autoComplete="off"
      />
      {statusMessage ? (
        <div style={{ marginTop: "6px", fontSize: "12px", color: "#b91c1c" }}>
          {statusMessage}
        </div>
      ) : null}
    </div>
  );
});

export default AddressAutocompleteInput;
